import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import type { AdminUserListItem, ApprovalStatus } from '../../types/users';

/**
 * Stesso stato per piu' utenti selezionati. Approvando si possono abilitare
 * dei tool in aggiunta a quelli che ciascuno ha gia'; rifiutando serve un motivo.
 */
interface Props {
    users: AdminUserListItem[];
    action: ApprovalStatus | null;
    onClose: () => void;
    onDone: (updated: AdminUserListItem[]) => void;
}

interface ToolOption { id: number; name: string; code: string; status: string; visibility: string }

const COPY: Record<ApprovalStatus, { title: string; button: string; tone: string }> = {
    Approved: { title: 'Approva utenti', button: 'Approva', tone: 'btn-primary' },
    Suspended: { title: 'Sospendi utenti', button: 'Sospendi', tone: 'btn-danger' },
    Rejected: { title: 'Rifiuta utenti', button: 'Rifiuta', tone: 'btn-danger' },
    Pending: { title: 'Rimetti in attesa', button: 'Rimetti in attesa', tone: 'btn-primary' },
};

const BulkApprovalModal: React.FC<Props> = ({ users, action, onClose, onDone }) => {
    const { showToast } = useToast();
    const [tools, setTools] = useState<ToolOption[]>([]);
    const [toolIds, setToolIds] = useState<number[]>([]);
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (action !== 'Approved') return;
        setToolIds([]);
        apiService.adminGetTools<ToolOption>()
            .then(list => setTools(list.filter(t => t.status === 'Active' || t.status === 'Debug')))
            .catch(() => setTools([]));
    }, [action]);

    useEffect(() => { setReason(''); }, [action]);

    const restricted = useMemo(() => tools.filter(t => t.visibility === 'Restricted'), [tools]);
    const others = useMemo(() => tools.filter(t => t.visibility !== 'Restricted'), [tools]);

    if (!action) return null;
    const copy = COPY[action];

    const submit = async () => {
        setSaving(true);
        try {
            const result = await apiService.adminBulkUserApproval<{ updated: AdminUserListItem[]; skipped: string[] }>({
                userIds: users.map(u => u.id),
                approvalStatus: action,
                rejectedReason: action === 'Rejected' ? reason.trim() : undefined,
                addToolIds: action === 'Approved' && toolIds.length ? toolIds : undefined,
            });
            const n = result.updated.length;
            showToast('Operazione completata',
                `${n} ${n === 1 ? 'utente aggiornato' : 'utenti aggiornati'}${result.skipped.length ? ` · ${result.skipped.length} saltati` : ''}`,
                result.skipped.length ? 'warning' : 'success');
            result.skipped.forEach(s => showToast('Utente saltato', s, 'warning'));
            onDone(result.updated);
        } catch (e: any) {
            showToast('Errore', e?.response?.data?.error ?? 'Operazione non riuscita', 'danger');
        } finally {
            setSaving(false);
        }
    };

    const toggleTool = (id: number) =>
        setToolIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

    const renderTools = (list: ToolOption[]) => list.map(t => (
        <label key={t.id} className="list-group-item d-flex align-items-center gap-2 py-2">
            <input type="checkbox" className="form-check-input m-0" checked={toolIds.includes(t.id)} onChange={() => toggleTool(t.id)} />
            <span className="flex-grow-1 small"><strong>{t.name}</strong> <span className="text-body-secondary">{t.code}</span></span>
        </label>
    ));

    return (
        <Modal show onHide={() => !saving && onClose()} centered scrollable size={action === 'Approved' ? 'lg' : undefined}>
            <Modal.Header closeButton={!saving}>
                <Modal.Title>{copy.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-2">
                    <strong>{users.length}</strong> {users.length === 1 ? 'utente selezionato' : 'utenti selezionati'}:
                </p>
                <div className="d-flex flex-wrap gap-1 mb-3">
                    {users.slice(0, 12).map(u => <span key={u.id} className="badge bg-light">{u.email}</span>)}
                    {users.length > 12 && <span className="badge bg-light">+{users.length - 12}</span>}
                </div>

                {action === 'Approved' && (
                    <>
                        <div className="form-label">Abilita anche questi tool <span className="text-body-secondary fw-normal">(facoltativo)</span></div>
                        <p className="small text-body-secondary">
                            Si aggiungono ai tool che ciascun utente ha già. I tool pubblici sono comunque visibili in base al profilo.
                        </p>
                        {restricted.length > 0 && <>
                            <div className="small fw-semibold mb-1"><i className="bi bi-lock-fill me-1" />Ristretti</div>
                            <div className="list-group mb-3">{renderTools(restricted)}</div>
                        </>}
                        {others.length > 0 && <>
                            <div className="small fw-semibold mb-1">Pubblici</div>
                            <div className="list-group">{renderTools(others)}</div>
                        </>}
                    </>
                )}

                {action === 'Rejected' && (
                    <>
                        <label className="form-label" htmlFor="bulk-reason">Motivo del rifiuto</label>
                        <textarea id="bulk-reason" className="form-control" rows={3} maxLength={500}
                            placeholder="Es. registrazione non riconducibile a un cliente attivo"
                            value={reason} onChange={e => setReason(e.target.value)} />
                        <div className="form-text">Resta registrato sull’utente.</div>
                    </>
                )}

                {action === 'Suspended' && (
                    <div className="alert alert-warning mb-0">
                        Gli utenti sospesi non possono più usare il portale finché non vengono riattivati.
                        Il tuo account, se selezionato, viene saltato.
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Annulla</button>
                <button className={`btn ${copy.tone}`} onClick={() => void submit()}
                    disabled={saving || (action === 'Rejected' && !reason.trim())}>
                    {saving ? 'Applico…' : `${copy.button} (${users.length})`}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default BulkApprovalModal;
